import { Canvas, ThreeEvent } from "@react-three/fiber";
import './process-editor.css';
import { POI } from "../poi/poi";
import { BoxHelper } from "./box-helper/box-helper";
import { Menu } from "./menu/menu";
import { Model as RenderedModel } from './model/model';
import { ProcessEditorExposerRef, ProcessEditorRefExposer } from "./process-editor-ref-exposer/process-editor-ref-exposer";
import { forwardRef, useImperativeHandle, useRef } from "react";
import { WorkInstructionWithUUIDs } from "../model-space/model-space";
import { TransparentModel } from "./transparent-model/transparent-model";
import * as THREE from 'three';
import { OrbitControls, ProcessEditorOrbitControlsRef } from "./controls/orbit-controls";
import { TransformControls } from "./controls/transform-controls";
import { ViewportShading, ViewportShadingSelect } from "./viewport-shading-select/viewport-shading-select";
import { Model } from "../../types/api/model/get-models-success-response";
import { ModelInModelTreeInProcessEditor } from "../../utils/create-model-tree-for-porcess-editor-and-set-uuid/create-model-tree-for-porcess-editor-and-set-uuid";
import { NewProcessStepItemWithEndpointModel, ProcessStepItemWithEndpointModel } from "../../types/api/process-step-item/process-step-item";
import { cloneModel } from "../../utils/clone-model/clone-model";

export const DEFAULT_CAMERA_POSITION = new THREE.Vector3(2, 2, 10);

export type ProcessEditorRef = ProcessEditorExposerRef & ProcessEditorOrbitControlsRef;

export type ProcessEditorActionStatus =
'clickOnStartingPointMenuItem' |
'selectingStartingModel' |
'selectingReferenceModel' |
'selectingTool' |
'clickOnEndpointMenuItem' |
'settingEndpointModel' |
'clickToDone' |
'done';

type ProcessEditorProps = {
    models: Model[];
    selectedModelUUID: string | null;
    workInstructions: WorkInstructionWithUUIDs[];
    isPOIActive: boolean;
    onSelectModel: (uuid: string | null) => void;
    onModelChange?: (uuid: string, data: {
        xPosition: number;
        yPosition: number;
        zPosition: number;
        xRotation: number;
        yRotation: number;
        zRotation: number;
    }) => void;
    instruction?: string | null;
    onModelLoaded?: (successfullyLoaded: boolean, model: ModelInModelTreeInProcessEditor) => void;
    selectedProcessStepItem?: ProcessStepItemWithEndpointModel | NewProcessStepItemWithEndpointModel;
    enableTransformControlsOnSelectedModel: boolean;
    viewportShading?: ViewportShading;
    onViewportShadingChange?: (viewportShading: ViewportShading) => void;
};

export const ProcessEditor = forwardRef<ProcessEditorRef, ProcessEditorProps>((
    {
        models,
        isPOIActive,
        selectedModelUUID,
        workInstructions,
        onSelectModel,
        instruction,
        onModelChange,
        onModelLoaded,
        selectedProcessStepItem,
        enableTransformControlsOnSelectedModel,
        viewportShading,
        onViewportShadingChange
    },
    processEditorRef
) => {
    const currentEndpointModelUUID = useRef<string | null>(null);
    const currentEndpointModel = useRef<THREE.Object3D>();

    const processEditorExposerRef = useRef<ProcessEditorExposerRef>();
    const orbitControlsRef = useRef<ProcessEditorOrbitControlsRef>();
    useImperativeHandle(processEditorRef, () => ({
        setModelPositionAndRotation: (uuid: string, position: THREE.Vector3Tuple, rotation: THREE.EulerTuple) => {
            if (processEditorExposerRef.current)
                processEditorExposerRef.current.setModelPositionAndRotation(uuid, position, rotation);
        },
        setModelScale: (uuid: string, scale: THREE.Vector3Tuple) => {
            if (processEditorExposerRef.current) {
                processEditorExposerRef.current.setModelScale(uuid, scale);
            }
        },
        getModelByUUID: (uuid: string) => {
            if (processEditorExposerRef.current)
                return processEditorExposerRef.current.getModelByUUID(uuid);

            return undefined;
        },
        getScene: () => {
            if (processEditorExposerRef.current)
                return processEditorExposerRef.current.getScene();

            return undefined;
        },
        getRootModelUUIDByUUID: (uuid: string) => {
            if (processEditorExposerRef.current)
                return processEditorExposerRef.current.getRootModelUUIDByUUID(uuid);

            return undefined;
        },
        getModelWorldPositionAndRotationByUUID: (uuid: string) => {
            if (processEditorExposerRef.current)
                return processEditorExposerRef.current.getModelWorldPositionAndRotationByUUID(uuid);

            return undefined;
        },
        setWorldPositionAndRotationAsLocalByUUID: (
            uuid: string,
            worldPosition: THREE.Vector3,
            worldRotation: THREE.Euler
        ) => {
            if (processEditorExposerRef.current)
                return processEditorExposerRef.current.setWorldPositionAndRotationAsLocalByUUID(uuid, worldPosition, worldRotation);
            
            return undefined;
        },
        getModelName: (uuid: string) => {
            if (processEditorExposerRef.current)
                return processEditorExposerRef.current.getModelName(uuid);

            return undefined;
        },
        setModelMaterial: (uuid: string, material: THREE.Material) => {
            if (processEditorExposerRef.current) processEditorExposerRef.current.setModelMaterial(uuid, material);
        },
        changeModelvisibility: (uuid: string, visible: boolean) => {
            if (processEditorExposerRef.current) return processEditorExposerRef.current.changeModelvisibility(uuid, visible);
        },
        getModelvisibility: (uuid: string) => {
            if (processEditorExposerRef.current) return processEditorExposerRef.current.getModelvisibility(uuid);
            
            return false;
        },
        attachToParent: (childUUID, parentUUID) => {
            if (processEditorExposerRef.current) return processEditorExposerRef.current.attachToParent(childUUID, parentUUID);
        },
        setModelCenterToOrbitControlsTarget: (uuid: string) => {
            if (orbitControlsRef.current)
                orbitControlsRef.current.setModelCenterToOrbitControlsTarget(uuid);
        },
        zoomToModel: (uuid: string) => {
            if (orbitControlsRef.current) orbitControlsRef.current.zoomToModel(uuid);
        },
        setCameraPosition: (position: THREE.Vector3) => {
            if (orbitControlsRef.current) orbitControlsRef.current.setCameraPosition(position);
        }
    }), []);

    const createPOI = (workInstruction: WorkInstructionWithUUIDs) => {
        return (
            <POI
                key={workInstruction.id}
                POIUUID={workInstruction.POIUUID}
                POILineEndpointUUID={workInstruction.POILineEndpointUUID}
                workInstruction={workInstruction}
            />
        );
    };

    const createModel = (model: Model) => {
        if (!model.modifiedModel.url) return null;

        const handleClick = (e: ThreeEvent<MouseEvent>) => {
            e.stopPropagation();

            if (e.object) {
                setTimeout(() => {
                    onSelectModel(e.object.uuid);
                }, 300);
            }
        };

        const handleDoubleClick = (e: ThreeEvent<MouseEvent>) => {
            e.stopPropagation();

            if (e.object) {
                setTimeout(() => {
                    onSelectModel(e.object.uuid);
                    if (orbitControlsRef.current) orbitControlsRef.current.zoomToModel(e.object.uuid);
                }, 300);
            }
        };

        const handlePointerMissed = () => {
            setTimeout(() => {
                onSelectModel(null);
            }, 150);
        };

        const handleModelLoaded = (successfullyLoaded: boolean, model: ModelInModelTreeInProcessEditor) => {
            if (onModelLoaded) onModelLoaded(successfullyLoaded, model);
        };

        return (
            <RenderedModel
                key={model.uuid}
                uuid={model.uuid}
                visible={model.visible}
                name={model.name}
                position={[model.xPosition, model.yPosition, model.zPosition]}
                rotation={[model.xRotation, model.yRotation, model.zRotation]}
                scale={[model.xScale, model.yScale, model.zScale]}
                model={model.modifiedModel.url}
                onClick={handleClick}
                onPointerMissed={handlePointerMissed}
                onModelLoaded={handleModelLoaded}
                onDoubleClick={handleDoubleClick}
            />
        );
    };

    const handleModelChange = (
        uuid: string,
        xPosition: number,
        yPosition: number,
        zPosition: number,
        xRotation: number,
        yRotation: number,
        zRotation: number
    ) => {
        if (onModelChange) onModelChange(uuid, { xPosition, yPosition, zPosition, xRotation, yRotation, zRotation });
    };

    const createEndpointModel = (processStepItem: ProcessStepItemWithEndpointModel | NewProcessStepItemWithEndpointModel) => {
        if ((processEditorRef as React.MutableRefObject<ProcessEditorRef>).current &&
            processStepItem.modelToBeMovedUUID &&
            processStepItem.endpointModelUUID
        ) {

            if (currentEndpointModelUUID.current !== processStepItem.endpointModelUUID) {
                currentEndpointModelUUID.current = processStepItem.endpointModelUUID;

                const rootModelUUID = (processEditorRef as React.MutableRefObject<ProcessEditorRef>).current.getRootModelUUIDByUUID(processStepItem.modelToBeMovedUUID);
                if (rootModelUUID) {
                    let rootModel = (processEditorRef as React.MutableRefObject<ProcessEditorRef>).current.getModelByUUID(rootModelUUID);
                    if (rootModel) {
                        rootModel.getObjectByProperty('uuid', processStepItem.modelToBeMovedUUID)!.name = processStepItem.modelToBeMovedUUID;
                        currentEndpointModel.current = cloneModel(rootModel).getObjectByName(processStepItem.modelToBeMovedUUID);
                        currentEndpointModel.current?.position.set(0, 0, 0);
                        currentEndpointModel.current?.scale.copy(rootModel.getObjectByProperty('uuid', processStepItem.modelToBeMovedUUID)!.getWorldScale(new THREE.Vector3()));
                    }
                }
            }

            if (currentEndpointModel.current && currentEndpointModelUUID.current) {
                return (
                    <TransparentModel
                        uuid={currentEndpointModelUUID.current}
                        model={currentEndpointModel.current}
                        position={[processStepItem.targetXPosition, processStepItem.targetYPosition, processStepItem.targetZPosition]}
                        rotation={[processStepItem.targetXRotation, processStepItem.targetYRotation, processStepItem.targetZRotation]}
                    />
                );
            }
        }
        
        return null;
    };

    return (
        <div className="process-editor-container">

            <Menu />

            {
                instruction && (
                    <div className="instruction">
                        { instruction }
                    </div>
                )
            }

            <div className="position-relative">

                {
                    viewportShading && (
                        <div className="viewport-shading-select-container">
                            <ViewportShadingSelect
                                viewportShading={viewportShading}
                                onViewportShadingChange={
                                    (viewportShading) => {
                                        if (onViewportShadingChange) onViewportShadingChange(viewportShading);
                                    }
                                }
                            />
                        </div>
                    )
                }

                <Canvas
                    className="canvas"
                    camera={
                        {
                            position: DEFAULT_CAMERA_POSITION,
                            near: 0.01,
                            far: 1000,
                            fov: 50
                        }
                    }
                >

                    {
                        isPOIActive && workInstructions && workInstructions.map(workInstruction => createPOI(workInstruction))
                    }

                    { models.map(model => createModel(model)) }

                    {
                    selectedProcessStepItem && createEndpointModel(selectedProcessStepItem)
                    }

                    <BoxHelper selectedUUID={selectedModelUUID} />
                    <OrbitControls selectedModelUUID={selectedModelUUID} ref={orbitControlsRef as React.LegacyRef<ProcessEditorOrbitControlsRef>} />
                    {
                        enableTransformControlsOnSelectedModel && (
                            <TransformControls
                                selectedModelUUID={selectedModelUUID}
                                skipSettingModelData={false}
                                onModelChange={handleModelChange}
                            />
                        )
                    }

                    <ambientLight />
                    <directionalLight position={new THREE.Vector3(0, 1000, 0)} />
                    <directionalLight position={new THREE.Vector3(0, -1000, 0)} />
                    <directionalLight position={new THREE.Vector3(1000, 0, 0)} />
                    <directionalLight position={new THREE.Vector3(-1000, 0, 0)} />
                    <directionalLight position={new THREE.Vector3(0, 0, 1000)} />
                    <directionalLight position={new THREE.Vector3(0, 0, -1000)} />

                    <gridHelper />
                    <color attach='background' args={['#ffffffff']} />
                    <ProcessEditorRefExposer ref={processEditorExposerRef} />

                </Canvas>

            </div>

        </div>
    );
});